import React, { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * LayoutTemplatesModal — owner asked: "save the entire mirror's card
 * arrangement as a named template ('Morning Mode', 'Trading Mode',
 * 'Evening Mode') and switch between them with one voice command".
 *
 * Reads localStorage `rawco-proof-positions-main` and
 * `rawco-proof-positions-mirror` for the current positions, persists
 * named snapshots to the backend via /api/jarvis/chat → save_layout
 * action.
 */
export default function LayoutTemplatesModal({ open, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchAll = async () => {
    try {
      const r = await api.post("/jarvis/chat", { message: "list my layouts" });
      const list = r.data?.action?.result?.templates || [];
      setTemplates(list);
    } catch { setTemplates([]); }
  };

  useEffect(() => { if (open) fetchAll(); }, [open]);

  if (!open) return null;

  const currentPositions = () => {
    const out = {};
    for (const mode of ["main", "mirror"]) {
      try {
        const raw = localStorage.getItem(`rawco-proof-positions-${mode}`);
        if (raw) out[mode] = JSON.parse(raw) || {};
      } catch { /* ignore */ }
    }
    return out;
  };

  const save = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post("/jarvis/chat", {
        message: `__save_layout__:${name}`,
        intent_hint: "save_layout",
      });
      // Fallback: also call swarm-style via execute since the chat path
      // doesn't always carry the positions field. Just hit /jarvis/agents/
      // … or use direct DB? Cleanest: a small endpoint. We use intent_hint
      // path which doesn't fit — fall back to a direct call below.
      // Actually we use a dedicated minimal endpoint via the executed action.
    } catch { /* ignore */ }
    // Direct save via /jarvis/save_layout
    try {
      await api.post("/jarvis/save_layout", { name, positions: currentPositions() });
    } catch (e) { console.warn("[layouts] save failed", e); }
    setBusy(false);
    setNewName("");
    fetchAll();
  };

  const apply = async (tpl) => {
    if (!tpl?.positions && !tpl?.theme) return;
    // Card positions
    if (tpl.positions) {
      for (const mode of ["main", "mirror"]) {
        const pos = tpl.positions[mode];
        if (pos && typeof pos === "object") {
          try { localStorage.setItem(`rawco-proof-positions-${mode}`, JSON.stringify(pos)); } catch {}
        }
      }
      try { window.dispatchEvent(new Event("rawco-positions-reload")); } catch {}
    }
    // 🎨 Theme — if this template captured a theme, apply it. Owner asked:
    // "each saved layout could even auto-set its own theme".
    if (tpl.theme && typeof tpl.theme === "object") {
      try {
        await api.post("/theme", tpl.theme);
        try { window.dispatchEvent(new Event("rawco-theme-changed")); } catch {}
      } catch (e) { console.warn("[layouts] theme apply failed", e); }
    }
    onClose?.();
  };

  const del = async (name) => {
    if (!window.confirm(`Delete layout "${name}"?`)) return;
    setBusy(true);
    try { await api.delete(`/jarvis/save_layout/${encodeURIComponent(name)}`); } catch {}
    setBusy(false);
    fetchAll();
  };

  return (
    <div
      className="fixed inset-0 z-[140] flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="layout-templates-backdrop"
    >
      <div
        className="rc-panel p-5 max-w-lg w-full mx-4"
        style={{ background: "rgba(2,4,12,0.96)" }}
        onClick={e => e.stopPropagation()}
        data-testid="layout-templates-modal"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="rc-label text-xs rc-glow-text">// LAYOUT TEMPLATES</div>
          <button onClick={onClose} className="text-[var(--rc-gold)] opacity-70 hover:opacity-100 text-lg leading-none" data-testid="layouts-close">✕</button>
        </div>

        <div className="text-[11px] text-[var(--rc-text-dim)] mb-3">
          Save the current card arrangement, or load a saved layout to swap the mirror over instantly.
        </div>

        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="e.g. Morning Mode"
            className="flex-1 bg-transparent border border-[var(--rc-gold-dim)] px-3 py-2 text-xs text-[var(--rc-text)] focus:border-[var(--rc-gold)] outline-none"
            data-testid="layout-name-input"
          />
          <button
            onClick={save}
            disabled={busy || !newName.trim()}
            className="rc-btn !text-[10px] !py-2 px-4"
            data-testid="layout-save-button"
          >
            {busy ? "…" : "💾 SAVE"}
          </button>
        </div>

        <div className="max-h-[55vh] overflow-y-auto">
          {templates.length === 0 ? (
            <div className="text-[11px] text-[var(--rc-text-dim)] py-4 text-center">
              No layouts saved yet. Drag your cards around, then save them as a template.
            </div>
          ) : (
            templates.map((tpl) => (
              <div key={tpl.name} className="flex items-center justify-between py-2 border-b border-[var(--rc-gold-dim)]/30"
                   data-testid={`layout-row-${tpl.name}`}>
                <div className="flex-1">
                  <div className="text-xs">{tpl.name}</div>
                  <div className="text-[9px] text-[var(--rc-text-dim)]">
                    Saved {tpl.updated_at ? new Date(tpl.updated_at).toLocaleString() : "—"}
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => apply(tpl)} className="rc-btn !text-[10px] !py-1 px-2" data-testid={`layout-apply-${tpl.name}`}>APPLY</button>
                  <button onClick={() => del(tpl.name)} className="border border-red-500/40 text-red-400 text-[10px] py-1 px-2 hover:bg-red-500/10" data-testid={`layout-del-${tpl.name}`}>✕</button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
